'use client';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import classnames from 'classnames';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { Dispatch, Fragment, KeyboardEvent, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { ActionIcon } from '../button/ActionIcon';
import { withOpacity } from '../helpers/color-helper';
import { IconClose24 } from '../icons/ic-close';
import { TooltipDirection } from '../tooltip/Tooltip';

import { isMdxPopupOpenOrJustClosed } from './mdx-popup-state';

export type ModalProps = {
    children?: React.ReactNode;
    classNameDialog?: string;
    className?: string;
    styleDialogPanel?: CSSProperties;
    /** @deprecated Use classNameDialog */
    zIndex?: number;
    title?: string | React.JSX.Element;
    titleCenter?: boolean;
    titleClassName?: string;
    subTitle?: string | React.JSX.Element;
    subTitleClassName?: string;
    modalIsOpen?: boolean;
    setModalIsOpen?: Dispatch<SetStateAction<boolean>>;
    closeable?: boolean;
    showCloseButton?: boolean;
    showBackground?: boolean;
    showBackgroundBlur?: boolean;
    showBackgroundImage?: boolean;
    backgroundColor?: string;
    enableOutsideActions?: boolean;
    /** `null` suppresses the default close (router.back()); `undefined` keeps it. */
    onClose?: (() => void) | null;
    beforeClose?: () => boolean | void;
    allowEnter?: boolean;
    trailingElements?: React.JSX.Element;
    /**
     * Optional footer rendered OUTSIDE the scrollable body, as a direct child of the
     * padded panel. Stays fixed while the body scrolls, and (unlike body content) its
     * children may break out to the full panel width via negative horizontal margins.
     */
    footer?: React.ReactNode;
    /**
     * Disables the ✕ — a real `disabled` on the button, not a click-swallowing
     * overlay (CIN-14887). For a modal that must not be dismissed while something is
     * in flight. `beforeClose` still guards the other close paths (backdrop, Escape).
     */
    closeButtonDisabled?: boolean;
    /**
     * Reports whether the scrollable body has content out of view above or below it,
     * so a caller's sticky title or footer can carry a shadow only while there is
     * something behind it (CIN-14887).
     *
     * The body's scroll container belongs to this component, so the observation
     * belongs here too — callers were locating it with
     * `node.closest('.overflow-y-auto')` and wiring their own listeners, keyed on an
     * internal utility class. Every modal with a sticky footer would otherwise copy
     * those forty lines.
     *
     * Called only when one of the two flags actually flips, so a caller can put the
     * result straight into state without a re-render per scroll event.
     */
    onBodyScrollStateChange?: (state: { hasContentAbove: boolean; hasContentBelow: boolean }) => void;
};

function Modal(props: ModalProps): React.JSX.Element | null {
    const { classNameDialog, className, styleDialogPanel, onClose, beforeClose, allowEnter } = props;

    const t = useTranslations();

    const router = useRouter();

    let modalIsOpen = props?.modalIsOpen;
    let setModalIsOpen = props?.setModalIsOpen;
    const [state, setState] = useState<boolean>(true);
    if (modalIsOpen === undefined && setModalIsOpen === undefined) {
        modalIsOpen = state;
        setModalIsOpen = setState;
    }

    const closeable = props.closeable !== false;

    // Held in a ref so the observers below are wired once per mount rather than
    // re-registered whenever a caller passes a fresh arrow (CIN-14887).
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const onBodyScrollStateChangeRef = useRef(props.onBodyScrollStateChange);
    useEffect(() => {
        onBodyScrollStateChangeRef.current = props.onBodyScrollStateChange;
    }, [props.onBodyScrollStateChange]);

    const reportsBodyScrollState = props.onBodyScrollStateChange !== undefined;
    useEffect(() => {
        const scroller = bodyRef.current;
        if (!scroller || !reportsBodyScrollState) {
            return;
        }

        let hasContentAbove = false;
        let hasContentBelow = false;
        // The 2px slack absorbs sub-pixel scroll positions, which would otherwise
        // report content above at the very top of an unscrolled body.
        function update() {
            const nextAbove = scroller!.scrollTop > 2;
            const nextBelow = scroller!.scrollTop + scroller!.clientHeight < scroller!.scrollHeight - 2;
            if (nextAbove === hasContentAbove && nextBelow === hasContentBelow) {
                return;
            }
            hasContentAbove = nextAbove;
            hasContentBelow = nextBelow;
            onBodyScrollStateChangeRef.current?.({ hasContentAbove, hasContentBelow });
        }

        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(scroller);
        // A ResizeObserver on the scroller sees its own box, not its content height,
        // so the children are observed as well — and re-observed on every DOM change,
        // because content that mounts later (a nested confirm, a preview appearing
        // after an upload) is otherwise never measured. observe() on an already
        // observed element is a no-op, so re-running this is cheap.
        function observeChildren() {
            for (const child of Array.from(scroller!.children)) {
                resizeObserver.observe(child);
            }
        }
        observeChildren();
        const mutationObserver = new MutationObserver(() => {
            observeChildren();
            update();
        });
        mutationObserver.observe(scroller, { childList: true, subtree: true });
        scroller.addEventListener('scroll', update, { passive: true });
        update();

        return () => {
            scroller.removeEventListener('scroll', update);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [reportsBodyScrollState, modalIsOpen]);

    const showCloseButton = closeable && props.showCloseButton !== false;

    const trailingElements: React.JSX.Element[] = useMemo(() => {
        const elements: React.JSX.Element[] = [];
        if (props.trailingElements) {
            elements.push(props.trailingElements);
        }
        return elements;
    }, [props.trailingElements, closeable]);

    function onCloseInternal() {
        // An MDX editor popup on screen, or one dismissed a moment ago, means this close request
        // came from that popup and not from the operator leaving — see mdx-popup-state. Inert for
        // every modal without such an editor, since then no popup container exists.
        if (isMdxPopupOpenOrJustClosed()) {
            return;
        }
        if (beforeClose && beforeClose() === false) {
            return;
        }
        setModalIsOpen?.(false);
        if (onClose) {
            onClose();
        } else if (onClose === undefined) {
            router.back();
        }
    }

    if (modalIsOpen === false) {
        return null;
    }

    return (
        <Dialog
            open={modalIsOpen}
            onClose={() => {
                if (closeable) {
                    onCloseInternal();
                }
            }}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (allowEnter || e.key !== 'Enter') {
                    return;
                }
                // Only suppress the implicit form-submit that a bare Enter triggers from within a
                // single-line text input. Enter must still activate a focused button, link, etc.
                const target = e.target as HTMLElement;
                if (target.tagName === 'INPUT') {
                    const inputType = (target as HTMLInputElement).type;
                    const nonSubmittingTypes = ['button', 'submit', 'reset', 'checkbox', 'radio', 'file'];
                    if (!nonSubmittingTypes.includes(inputType)) {
                        e.preventDefault();
                    }
                }
            }}
            className={classnames('relative z-[9000]', classNameDialog, {
                'pointer-events-none': props.enableOutsideActions
            })}>
            <DialogBackdrop
                className={classnames('fixed inset-0 transition-all duration-300', {
                    'bg-overlay': props.showBackground !== false,
                    'backdrop-blur-sm': props.showBackgroundBlur !== false,
                    [props.backgroundColor || '']: !props.backgroundColor?.startsWith('#')
                })}
                style={{
                    backgroundColor: props.backgroundColor?.startsWith('#')
                        ? (withOpacity(props.backgroundColor, 0.8) ?? undefined)
                        : undefined
                }}
            />

            <div className="fixed inset-0 flex w-screen items-center justify-center p-[9px] md:p-24">
                <DialogPanel
                    className={classnames(
                        className,
                        'relative bg-[var(--non-transparent-surface-color)] flex flex-col text-[var(--on-non-transparent-surface-color)]',
                        'w-full',
                        'min-h-[120px] max-h-[100%] overflow-hidden',
                        'flex flex-col p-[16px] md:p-[24px]',
                        'modal-window',
                        'rounded-8 relative',
                        {
                            'pointer-events-auto': props.enableOutsideActions
                        }
                    )}
                    style={styleDialogPanel}>
                    {(props.title || props.subTitle || showCloseButton) && (
                        <DialogTitle className="flex flex-col pb-[18px] md:pb-[16px] space-y-[16px]">
                            <div className={classnames('flex flex-row flex-none')}>
                                <div
                                    className={classnames('flex flex-col flex-auto', {
                                        'text-center mx-auto': props.titleCenter
                                    })}>
                                    {props.title && (
                                        <span
                                            className={classnames(
                                                props.titleClassName || 'text-headline-20 lg:text-headline-24'
                                            )}>
                                            {props.title}
                                        </span>
                                    )}
                                    {props.subTitle && (
                                        <div
                                            className={classnames(
                                                props.subTitleClassName ||
                                                    'text-body-bold-14 lg:text-headline-18 tracking-wide text-[rgba(var(--on-non-transparent-surface-color-rgb),.5)]'
                                            )}>
                                            {props.subTitle}
                                        </div>
                                    )}
                                </div>

                                {showCloseButton && (
                                    <div
                                        className="text-[var(--on-non-transparent-surface-color)] opacity-50 self-start"
                                        key={'close'}>
                                        <ActionIcon
                                            icon={<IconClose24 />}
                                            action={onCloseInternal}
                                            disabled={props.closeButtonDisabled}
                                            tooltipDirection={TooltipDirection.Top}
                                            tooltipText={t('common.close')}
                                        />
                                    </div>
                                )}
                            </div>

                            {trailingElements?.length > 0 && (
                                <div className="flex flex-row justify-end space-x-24 items-center">
                                    {trailingElements.map((element, index) => (
                                        <Fragment key={index}>{element}</Fragment>
                                    ))}
                                </div>
                            )}
                        </DialogTitle>
                    )}

                    <div
                        ref={bodyRef}
                        className="w-full h-auto flex flex-col max-h-full overflow-y-auto flex-auto">
                        {props.children}
                    </div>

                    {props.footer && <div className="flex-none">{props.footer}</div>}
                </DialogPanel>
            </div>
        </Dialog>
    );
}

export default Modal;
